import { negate, omit, range, set, flatten, map } from "lodash/fp";
import {
  useOrderFetchData,
  useReturnStepsState,
} from "../contexts/returnSteps";
import { useCallback } from "react";
import { RETURN_CREDIT_METHOD_REPLACEMENT } from "../../retailer-app/constants/returns";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "../../shared/modules/object";

const transformReturnItemToExchangeItem = (order, returnItemInput) => {
  const item = order?.itemsById?.[returnItemInput.lineItemId] ?? EMPTY_OBJECT;
  return {
    returnItem: returnItemInput,
    newProductId: item.productId,
    newProductVariantId: item.variantId,
    newProductVariantInfo: item.variantInfo.map(omit("__typename")),
    newProductSku: item.sku,
    newProductName: item.productTitle,
    newProductImage: item.productImageUrl,
    newProductPriceAmount: item.priceAmount,
    newProductPriceCurrency: order.presentmentCurrency,
  };
};

const useClaims = () => {
  const state = useReturnStepsState();
  const fetchData = useOrderFetchData();
  const order = fetchData.state.data?.order;
  const fulfillments = order?.fulfillments ?? EMPTY_ARRAY;
  const returnReasonsById = fetchData.state.data?.returnReasonsById;
  const insuranceProvider = order?.insuranceProvider;
  const isClaimable = order?.items?.some(
    (item) =>
      item.hasClaimProtection ||
      item.eligibilityCriteria.some((criteria) => criteria.isEligibleForClaim),
  );

  const selectedClaimReason = state.items.find((item) => item.isClaim);
  const hasClaimItems = !!selectedClaimReason;
  const claimReasonCode = selectedClaimReason?.returnReason;
  const claimType = returnReasonsById?.[claimReasonCode]?.claimType;
  const isClaimReplacement =
    state?.selectedReturnCreditMethod?.id === RETURN_CREDIT_METHOD_REPLACEMENT;

  const decorateItemsWithFulfillment = useCallback(
    map((item) => ({
      ...item,
      shopifyFulfillmentId: fulfillments.find((f) =>
        f.fulfillmentLineItems.some((li) => li.lineItemGid === item.lineItemId),
      )?.shopifyFulfillmentId,
    })),
    [fulfillments],
  );

  const transformClaimItemsToExchangeItems = useCallback(
    ({ returnItems, exchangeItems, ...rest }) => {
      const isClaim = (item) =>
        !!returnReasonsById?.[item.returnReasonId]?.claimType;
      const isNotClaim = negate(isClaim);
      return {
        ...rest,
        returnItems: returnItems.filter(isNotClaim),
        exchangeItems: [
          ...exchangeItems,
          ...flatten(
            returnItems
              .filter(isClaim)
              .map((item) => transformReturnItemToExchangeItem(order, item))
              .map((item) =>
                // Due to backend limitation, we convert a exchange with quantity > 1
                // into multiple exchanges which quantity is 1
                range(0, item.returnItem.quantity).map(() =>
                  set("returnItem.quantity", 1, item),
                ),
              ),
          ),
        ],
      };
    },
    [returnReasonsById],
  );
  const decorateClaimsSubmitPayload = useCallback(
    (payload) => {
      let ret = set(
        "returnItems",
        decorateItemsWithFulfillment(payload.returnItems),
      )(payload);
      ret = isClaimReplacement ? transformClaimItemsToExchangeItems(ret) : ret;
      return ret;
    },
    [
      isClaimReplacement,
      decorateItemsWithFulfillment,
      transformClaimItemsToExchangeItems,
    ],
  );

  const transformClaimSelectedItemsForNth = useCallback(
    (selectedItems) => {
      return selectedItems.map((item) => {
        return {
          ...item,
          type: isClaimReplacement ? "exchange" : item.type,
          exchangeTo: isClaimReplacement
            ? {
                variantInfo: item.variantInfo,
                productId: item.productId,
                variantId: item.variantId,
                name: item.name,
                price: item.price,
                sku: item.sku,
                imageUrl: item.imageUrl,
                available: true,
                availabilityStatus: undefined,
                exchangeForCredit: false,
              }
            : item.exchangeTo,
        };
      });
    },
    [isClaimReplacement],
  );

  return {
    isClaimable,
    hasClaimItems,
    isClaimReplacement,
    claimReasonCode,
    claimType,
    insuranceProvider,
    transformClaimItemsToExchangeItems,
    transformClaimSelectedItemsForNth,
    decorateClaimsSubmitPayload,
  };
};

export default useClaims;
